import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import {
  SpaceBetween,
  FormField,
  Input,
  Autosuggest,
  Button,
  Box,
  Container,
  Header,
  Select,
} from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { Contact } from '@amzn/red-velvet-api';
import { OptionalLabel, EventStepProps } from './Common';
import { isValidEmail } from '../../../../utils/validation';
import { searchPeople } from '../../../../utils/papiSearch';
import { useFeatures, getEnabledFeatures } from '../../../../hooks/features';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { usePapiSuggestions } from '../../../../hooks/usePapiSuggestions';
import { formatContactDisplayName, personToContact } from '../../../../utils/contactUtils';
import {
  buildCountryCodeOptions,
  getNationalDigits,
  inferCountry,
  formatPhoneForStorage,
} from '../../../../utils/phoneUtils';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { FIELD_LIMITS } from '../../../../utils/fieldConstraints';
import { MAX_AMAZON_CONTACTS } from '../constants';

const PLUS_PREFIX_LENGTH = 1; // The "+" in "+{callingCode}{digits}"

export interface AliasSearchProps {
  suggestions: { value: string; label: string }[];
  loading: boolean;
  loadSuggestions: (filteringText: string, rowIndex: number) => void;
  getEmailForAlias: (alias: string) => string | undefined;
  getNameForAlias: (alias: string) => string | undefined;
  activeSearchIndex: number | null;
}

export function ContactsComponent({
  currentFields,
  updateFieldHandler,
  errors,
  isReadOnly,
  metricPrefix,
}: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const amazonContacts = currentFields.amazonContacts ?? [{ name: '', phone: '', email: '' }];
  const externalContacts = currentFields.externalContacts ?? [];

  const updateAmazonContacts = (contacts: Contact[]) => {
    updateFieldHandler({ ...currentFields, amazonContacts: contacts });
  };

  const updateExternalContacts = (contacts: Contact[]) => {
    updateFieldHandler({ ...currentFields, externalContacts: contacts });
  };

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  // Ref to always access latest currentFields from async callbacks
  const fieldsRef = useRef(currentFields);
  fieldsRef.current = currentFields;

  // Auto-add additional coordinators to amazonContacts when they're added
  const syncedCoordinators = useRef<Set<string>>(
    new Set(currentFields.additionalCoordinators ?? [])
  );

  useEffect(() => {
    if (isReadOnly) return;
    const coordinators = currentFields.additionalCoordinators ?? [];
    const existingAliases = new Set(
      (currentFields.amazonContacts ?? []).map((c) => c.alias).filter(Boolean)
    );
    const newAliases = coordinators.filter(
      (alias) => !existingAliases.has(alias) && !syncedCoordinators.current.has(alias)
    );
    if (newAliases.length === 0) return;

    // Mark all as synced immediately to prevent re-processing
    newAliases.forEach((alias) => syncedCoordinators.current.add(alias));

    (async () => {
      const newContacts: Contact[] = [];
      for (const alias of newAliases) {
        const people = await searchPeople(
          alias,
          queryClient,
          getEnabledFeatures(features, searchParams)
        );
        const person = people?.find((p) => p.alias === alias);
        if (person) {
          newContacts.push(personToContact(person, alias));
        }
      }
      if (newContacts.length === 0) return;
      const latest = fieldsRef.current;
      const current = latest.amazonContacts ?? [];
      // Avoid showing duplicate contacts when a coordinator was already added.
      const currentAliases = new Set(current.map((c) => c.alias).filter(Boolean));
      const deduped = newContacts.filter((c) => !currentAliases.has(c.alias));
      if (deduped.length === 0) return;
      const slotsAvailable = MAX_AMAZON_CONTACTS - current.length;
      if (slotsAvailable <= 0) return;
      updateFieldHandler({
        ...latest,
        amazonContacts: [...current, ...deduped.slice(0, slotsAvailable)],
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFields.additionalCoordinators]);

  const papiSearch = usePapiSuggestions();
  const activeSearchIndex = useRef<number | null>(null);

  const aliasSearch: AliasSearchProps = {
    suggestions: papiSearch.suggestions,
    loading: papiSearch.loading,
    loadSuggestions: (filteringText, rowIndex) => {
      activeSearchIndex.current = rowIndex;
      papiSearch.search(filteringText);
    },
    getEmailForAlias: papiSearch.getEmailForAlias,
    getNameForAlias: papiSearch.getNameForAlias,
    activeSearchIndex: activeSearchIndex.current,
  };

  return (
    <Container header={<Header variant="h2">{t('teamPartnershipStep.contacts')}</Header>}>
      <SpaceBetween size="xl">
        <ContactSection
          title={t('teamPartnershipStep.amazonContactsTitle')}
          description={t('teamPartnershipStep.amazonContactsDescription')}
          contacts={amazonContacts}
          onUpdate={updateAmazonContacts}
          addLabel={t('teamPartnershipStep.addAmazonContact')}
          nameLabel={t('teamPartnershipStep.contactNameAlias')}
          namePlaceholder={t('teamPartnershipStep.contactNameAliasPlaceholder')}
          showEmail={false}
          minRows={1}
          aliasSearch={aliasSearch}
          errors={errors}
          errorPrefix="amazonContact"
          isReadOnly={isReadOnly}
          metricPrefix={metricPrefix}
        />
        <ContactSection
          title={<OptionalLabel>{t('teamPartnershipStep.externalContactsTitle')}</OptionalLabel>}
          description={t('teamPartnershipStep.externalContactsDescription')}
          contacts={externalContacts}
          onUpdate={updateExternalContacts}
          addLabel={t('teamPartnershipStep.addExternalContact')}
          nameLabel={t('teamPartnershipStep.contactName')}
          namePlaceholder={t('teamPartnershipStep.contactNamePlaceholder')}
          showEmail={true}
          errors={errors}
          errorPrefix="externalContact"
          isReadOnly={isReadOnly}
          metricPrefix={metricPrefix}
        />
      </SpaceBetween>
    </Container>
  );
}

interface PendingInput {
  index: number;
  field: keyof Contact;
  value: string;
}

function ContactSection({
  title,
  description,
  contacts,
  onUpdate,
  addLabel,
  nameLabel,
  namePlaceholder,
  showEmail,
  minRows = 0,
  aliasSearch,
  errors,
  errorPrefix,
  isReadOnly,
  metricPrefix,
}: {
  title: ReactNode;
  description: string;
  contacts: Contact[];
  onUpdate: (contacts: Contact[]) => void;
  addLabel: string;
  nameLabel: string;
  namePlaceholder: string;
  showEmail: boolean;
  minRows?: number;
  aliasSearch?: AliasSearchProps;
  errors?: Record<string, string>;
  errorPrefix: string;
  isReadOnly?: boolean;
  metricPrefix: string;
}) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [pendingInput, setPendingInput] = useState<PendingInput | null>(null);
  const countryCodeOptions = useMemo(() => buildCountryCodeOptions(), []);

  // Tracks the user's explicit dropdown selection per row. Only consulted when the
  // phone field is still empty (user picked a country but hasn't typed digits yet).
  const [countryCodeOverrides, setCountryCodeOverrides] = useState<Record<number, CountryCode>>({});

  // Stored phone is the source of truth for country; the override is only a
  // placeholder until the user types and blurs.
  const getCountryCode = (index: number): CountryCode => {
    const phone = contacts[index]?.phone;
    if (phone) return inferCountry(phone);
    return countryCodeOverrides[index] ?? 'US';
  };

  // NOTE: dismissedErrors tracks prop-level errors the user has corrected locally.
  // It resets when the parent passes a new errors object reference (i.e., validation re-runs).
  const dismissedErrors = useRef<Set<string>>(new Set());
  const prevErrors = useRef(errors);
  if (errors !== prevErrors.current) {
    dismissedErrors.current.clear();
    prevErrors.current = errors;
  }
  const nextId = useRef(contacts.length);
  const contactRowKeys = useRef<number[]>(contacts.map((_, i) => i));

  // Keep React keys in sync when the parent provides a different contacts array
  // (e.g., loading a previously saved draft or restoring wizard state).
  if (contactRowKeys.current.length < contacts.length) {
    while (contactRowKeys.current.length < contacts.length) {
      contactRowKeys.current.push(nextId.current++);
    }
  } else if (contactRowKeys.current.length > contacts.length) {
    contactRowKeys.current.length = contacts.length;
  }

  const mergedErrors = {
    ...Object.fromEntries(
      Object.entries(errors ?? {}).filter(([key]) => !dismissedErrors.current.has(key))
    ),
    ...localErrors,
  };

  const getPhoneDigits = (index: number): string => {
    if (pendingInput?.index === index && pendingInput.field === 'phone') {
      return pendingInput.value;
    }
    const phone = contacts[index]?.phone ?? '';
    return getNationalDigits(phone, getCountryCode(index));
  };

  const getDisplayValue = (index: number, field: keyof Contact): string => {
    if (field === 'phone') return getPhoneDigits(index);
    if (pendingInput?.index === index && pendingInput.field === field) {
      return pendingInput.value;
    }
    const value = contacts[index]?.[field] ?? '';
    // Show "FirstName LastName (@alias)" in the UI for the name field
    if (field === 'name' && aliasSearch && contacts[index]?.alias) {
      return formatContactDisplayName(value, contacts[index].alias ?? '');
    }
    return value;
  };

  /** Resolves an alias to a full contact via the PAPI cache, or clears fields if unknown. */
  const resolveAlias = (alias: string, contact: Contact): Contact => {
    if (!aliasSearch) return { ...contact, name: alias };
    const email = aliasSearch.getEmailForAlias(alias);
    if (email) {
      return { ...contact, name: aliasSearch.getNameForAlias(alias) ?? '', email, alias };
    }
    return { ...contact, name: '', email: '', alias: undefined };
  };

  const handleChange = (index: number, field: keyof Contact, value: string) => {
    if (field === 'phone') value = value.replace(/\D/g, '');
    if (field === 'email') value = value.replace(/\s/g, '');
    if (field === 'name' && aliasSearch) aliasSearch.loadSuggestions(value, index);
    setPendingInput({ index, field, value });
  };

  const handleBlur = (index: number, field: keyof Contact) => {
    if (pendingInput?.index === index && pendingInput.field === field) {
      let trimmedValue = pendingInput.value.trim();
      if (field === 'phone') {
        trimmedValue = formatPhoneForStorage(getCountryCode(index), trimmedValue);
      }
      const updated = [...contacts];
      updated[index] = { ...contacts[index], [field]: trimmedValue };
      if (aliasSearch && field === 'name') {
        updated[index] = resolveAlias(trimmedValue, updated[index]);
      }
      onUpdate(updated);
      setPendingInput(null);
    }
    validateOnBlur(index, field, getDisplayValue(index, field));
  };

  const validatePhoneLength = (index: number, digits: string, country: CountryCode) => {
    const key = `${errorPrefix}_${index}_phone`;
    const callingCode = getCountryCallingCode(country);
    const storedLength = PLUS_PREFIX_LENGTH + callingCode.length + digits.length;
    if (digits && storedLength > FIELD_LIMITS.CONTACT_PHONE) {
      setLocalErrors((prev) => ({
        ...prev,
        [key]: t('error.inputTooLong', { maxLength: FIELD_LIMITS.CONTACT_PHONE }),
      }));
    } else {
      setLocalErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleCountryCodeChange = (index: number, countryCode: CountryCode) => {
    setCountryCodeOverrides((prev) => ({ ...prev, [index]: countryCode }));
    const digits = getPhoneDigits(index);
    if (digits) {
      const updated = [...contacts];
      updated[index] = { ...contacts[index], phone: formatPhoneForStorage(countryCode, digits) };
      onUpdate(updated);
    }
    validatePhoneLength(index, digits ?? '', countryCode);
  };

  const validateOnBlur = (index: number, field: keyof Contact, value: string) => {
    const key = `${errorPrefix}_${index}_${field}`;
    if (field === 'name' && !aliasSearch && value && value.length > FIELD_LIMITS.CONTACT_NAME) {
      setLocalErrors((prev) => ({
        ...prev,
        [key]: t('error.inputTooLong', { maxLength: FIELD_LIMITS.CONTACT_NAME }),
      }));
    } else if (field === 'email' && value && !isValidEmail(value)) {
      setLocalErrors((prev) => ({ ...prev, [key]: t('error.invalidEmail') }));
    } else if (field === 'email' && value && value.length > FIELD_LIMITS.CONTACT_EMAIL) {
      setLocalErrors((prev) => ({
        ...prev,
        [key]: t('error.inputTooLong', { maxLength: FIELD_LIMITS.CONTACT_EMAIL }),
      }));
    } else if (field === 'phone' && value) {
      validatePhoneLength(index, value, getCountryCode(index));
    } else {
      setLocalErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (value.trim() && errors?.[key]) {
        dismissedErrors.current.add(key);
      }
    }
  };

  const addContact = () => {
    if (contacts.length < MAX_AMAZON_CONTACTS) {
      const newIndex = contactRowKeys.current.length;
      contactRowKeys.current.push(nextId.current++);
      setCountryCodeOverrides((prev) => ({ ...prev, [newIndex]: 'US' as CountryCode }));
      onUpdate([...contacts, { name: '', phone: '', email: '' }]);
    }
  };

  const removeContact = (index: number) => {
    contactRowKeys.current.splice(index, 1);
    if (pendingInput?.index === index) setPendingInput(null);
    // Re-index overrides so they track their new positions.
    setCountryCodeOverrides((prev) => {
      const next: Record<number, CountryCode> = {};
      let j = 0;
      for (let i = 0; i < contacts.length; i++) {
        if (i !== index) {
          if (i in prev) next[j] = prev[i];
          j++;
        }
      }
      return next;
    });
    onUpdate(contacts.filter((_, i) => i !== index));
  };

  const renderNameField = (index: number) => {
    if (aliasSearch) {
      const isActiveRow = aliasSearch.activeSearchIndex === index;
      return (
        <Autosuggest
          filteringType="manual"
          options={isActiveRow ? aliasSearch.suggestions : []}
          statusType={isActiveRow && aliasSearch.loading ? 'loading' : undefined}
          placeholder={namePlaceholder}
          value={getDisplayValue(index, 'name')}
          onChange={({ detail }) => handleChange(index, 'name', detail.value)}
          onSelect={({ detail }) => {
            if (!detail.value || !aliasSearch.getEmailForAlias(detail.value)) return;
            const updated = [...contacts];
            updated[index] = resolveAlias(detail.value, contacts[index]);
            onUpdate(updated);
            setPendingInput(null);
          }}
          onBlur={() => handleBlur(index, 'name')}
          readOnly={isReadOnly}
        />
      );
    }
    return (
      <Input
        placeholder={namePlaceholder}
        value={getDisplayValue(index, 'name')}
        onChange={({ detail }) => handleChange(index, 'name', detail.value)}
        onBlur={() => handleBlur(index, 'name')}
        readOnly={isReadOnly}
      />
    );
  };

  const getCountryCodeOption = (index: number) => {
    const cc = getCountryCode(index);
    return countryCodeOptions.find((o) => o.value === cc) ?? { value: 'US', label: 'US (+1)' };
  };

  return (
    <SpaceBetween size="m">
      <Box>
        <Header description={description} variant="h3">
          {title}
        </Header>
      </Box>
      <SpaceBetween size="s">
        {contacts.map((contact, index) => {
          const hasRemove = !isReadOnly && contacts.length > minRows;
          const gridColumns = showEmail
            ? hasRemove
              ? '2fr 2fr 3fr auto'
              : '2fr 2fr 3fr'
            : hasRemove
              ? '1fr 2fr auto'
              : '1fr 2fr';
          return (
            <div
              key={contactRowKeys.current[index]}
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: '20px',
                alignItems: 'start',
              }}
            >
              <FormField
                label={nameLabel}
                errorText={(() => {
                  const err = mergedErrors?.[`${errorPrefix}_${index}_name`];
                  if (!err) return undefined;
                  if (!aliasSearch) {
                    const len = getDisplayValue(index, 'name').length;
                    if (len > FIELD_LIMITS.CONTACT_NAME) {
                      return `${err} ${t('error.characterCount')} ${len}/${FIELD_LIMITS.CONTACT_NAME}`;
                    }
                  }
                  return err;
                })()}
                constraintText={(() => {
                  if (aliasSearch) return undefined;
                  if (mergedErrors?.[`${errorPrefix}_${index}_name`]) return undefined;
                  const len = getDisplayValue(index, 'name').length;
                  if (!len) return undefined;
                  return `${t('error.characterCount')} ${len}/${FIELD_LIMITS.CONTACT_NAME}`;
                })()}
              >
                {renderNameField(index)}
              </FormField>
              {showEmail && (
                <FormField
                  label={t('teamPartnershipStep.contactEmail')}
                  errorText={(() => {
                    const err = mergedErrors?.[`${errorPrefix}_${index}_email`];
                    if (!err) return undefined;
                    const len = getDisplayValue(index, 'email').length;
                    if (len > FIELD_LIMITS.CONTACT_EMAIL) {
                      return `${err} ${t('error.characterCount')} ${len}/${FIELD_LIMITS.CONTACT_EMAIL}`;
                    }
                    return err;
                  })()}
                  constraintText={(() => {
                    if (mergedErrors?.[`${errorPrefix}_${index}_email`]) return undefined;
                    const len = getDisplayValue(index, 'email').length;
                    if (!len) return undefined;
                    return `${t('error.characterCount')} ${len}/${FIELD_LIMITS.CONTACT_EMAIL}`;
                  })()}
                >
                  <Input
                    placeholder={t('teamPartnershipStep.contactEmailPlaceholder')}
                    type="email"
                    value={getDisplayValue(index, 'email')}
                    onChange={({ detail }) => handleChange(index, 'email', detail.value)}
                    onBlur={() => handleBlur(index, 'email')}
                    readOnly={isReadOnly}
                  />
                </FormField>
              )}
              <FormField
                label={<OptionalLabel>{t('teamPartnershipStep.contactPhone')}</OptionalLabel>}
                errorText={(() => {
                  const err = mergedErrors?.[`${errorPrefix}_${index}_phone`];
                  if (!err) return undefined;
                  const digits = getPhoneDigits(index);
                  const cc = getCountryCallingCode(getCountryCode(index));
                  const count = `${t('error.characterCount')} ${PLUS_PREFIX_LENGTH + cc.length + (digits?.length ?? 0)}/${FIELD_LIMITS.CONTACT_PHONE}`;
                  return `${err} ${count}`;
                })()}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ width: '120px', flexShrink: 0 }}>
                    <Select
                      selectedOption={getCountryCodeOption(index)}
                      options={countryCodeOptions}
                      onChange={({ detail }) =>
                        handleCountryCodeChange(index, detail.selectedOption.value as CountryCode)
                      }
                      filteringType="auto"
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input
                      placeholder={t('teamPartnershipStep.contactPhonePlaceholder')}
                      value={getPhoneDigits(index)}
                      onChange={({ detail }) => handleChange(index, 'phone', detail.value)}
                      onBlur={() => handleBlur(index, 'phone')}
                      readOnly={isReadOnly}
                    />
                    {(() => {
                      if (mergedErrors?.[`${errorPrefix}_${index}_phone`]) return null;
                      const digits = getPhoneDigits(index);
                      if (!digits) return null;
                      const cc = getCountryCallingCode(getCountryCode(index));
                      return (
                        <Box
                          color="text-body-secondary"
                          fontSize="body-s"
                        >{`${t('error.characterCount')} ${PLUS_PREFIX_LENGTH + cc.length + digits.length}/${FIELD_LIMITS.CONTACT_PHONE}`}</Box>
                      );
                    })()}
                  </div>
                </div>
              </FormField>
              {hasRemove && (
                <FormField label="&nbsp;">
                  <Button
                    variant="normal"
                    onClick={() => removeContact(index)}
                    data-aci-analytics-name={`${metricPrefix}_contactRemoved`}
                  >
                    {t('teamPartnershipStep.remove')}
                  </Button>
                </FormField>
              )}
            </div>
          );
        })}
      </SpaceBetween>
      {!isReadOnly && (
        <SpaceBetween size="xs">
          <Button
            iconName="add-plus"
            variant="normal"
            onClick={addContact}
            disabled={contacts.length >= MAX_AMAZON_CONTACTS}
            data-aci-analytics-name={`${metricPrefix}_contactAdded`}
          >
            {addLabel}
          </Button>
          <Box color="text-body-secondary" variant="small">
            {t('teamPartnershipStep.maxContacts', { max: MAX_AMAZON_CONTACTS })}
          </Box>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
